import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment } from '@mui/material';

// Helper function to format a number with commas
const formatNumberWithCommas = (num) => {
  if (num === null || num === undefined || num === '') {
    return '';
  }
  const numStr = String(num);
  const parts = numStr.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

// Helper function to parse a string back to a number (removing commas)
const parseNumberWithoutCommas = (str) => {
  if (str === null || str === undefined || str === '') {
    return null;
  }
  const cleanStr = String(str).replace(/,/g, '');
  const parsed = parseFloat(cleanStr);
  return isNaN(parsed) ? null : parsed;
};

const CommaSeparatedInput = ({ value, onChange, ...rest }) => {
  // Internal state for what's displayed in the TextField (with commas)
  const [displayValue, setDisplayValue] = useState('');

  // Effect to update displayValue when the external 'value' prop changes
  // This is crucial if 'value' is controlled by Formik or a parent component
  useEffect(() => {
    if (value !== null && value !== undefined && value !== '') {
      setDisplayValue(formatNumberWithCommas(value));
    } else {
      setDisplayValue(''); // Handle initial or external empty state
    }
  }, [value]);

  const handleChange = (event) => {
    const inputString = event.target.value;
    // setDisplayValue(inputString); // Update display immediately for smooth typing

    const numericValue = parseNumberWithoutCommas(inputString);
    // Call the external onChange handler with the pure numeric value
    if (onChange) {
      // Mimic event object structure if the consumer expects it
      onChange({
        ...event, // Spread original event properties
        target: {
          ...event.target, // Spread original target properties
          value: numericValue, // Override with the numeric value
          name: event.target.name || rest.name, // Ensure name is passed
        }
      });
    }
  };

  const handleBlur = (event) => {
    // Re-format the displayed value when the field loses focus
    const formatted = formatNumberWithCommas(value); // Use the actual numeric value from props
    setDisplayValue(formatted);

    // If you need to trigger formik.handleBlur, you'd pass it here
    if (rest.onBlur) {
      rest.onBlur(event);
    }
  };

  return (
    <TextField
      variant="outlined"
      value={displayValue} // TextField always displays the formatted string
      onChange={handleChange}
      onBlur={handleBlur}
      type="text" // Essential for manual formatting and comma input
      placeholder="e.g., 1,000,000"
      fullWidth
      InputProps={{
        inputMode: 'numeric', // Suggests numeric keyboard on mobile
        pattern: '[0-9,.]*',   // Helps browser understand expected input
      }}
      {...rest} // Pass any other TextField props (like name, error, helperText etc.)
    />
  );
};

export default CommaSeparatedInput;